<template>
  <v-row v-if="subscriptionDetails.length">
    <v-col>
      <h2 class="mb-3 mt-4">{{ $t('menus.subscriptions.subscriptionDetails') }}</h2>
      <v-card elevation=0>
        <v-card-text>
          <v-row
            v-for="detail in subscriptionDetails"
            :key="detail.key"
            class="py-2"
          >
            <v-col cols="12" md="4" class="font-weight-medium">
              {{ detail.label }}
            </v-col>
            <v-col cols="12" md="8">
              {{ detail.value }}
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { format } from 'date-fns/format';
import { useSystemStore } from '@/stores/SystemStore.js';

const DATE_FORMAT = 'yyyy-MM-dd';

export default {
  name: 'SubscriptionDetailsCard',
  computed: {
    subscription: function() {
      const systemStore = useSystemStore();
      return systemStore.getSubscription;
    },
    subscriptionDetails: function() {
      return [
        {
          key: 'subscription-start-date',
          label: this.$t('menus.subscriptions.subscriptionStartDate'),
          value: this.formatSubscriptionDate(this.resolveSubscriptionDate([
            'subscriptionStartDate',
            'subscriptionStart',
            'periodStart',
            'startDate',
          ])),
        },
        {
          key: 'subscription-period-end-date',
          label: this.$t('menus.subscriptions.subscriptionPeriodEndDate'),
          value: this.formatSubscriptionDate(this.resolveSubscriptionDate([
            'periodEndDate',
            'periodEnd',
            'periodend',
          ])),
        },
        {
          key: 'subscription-end-date',
          label: this.$t('menus.subscriptions.subscriptionEndDate'),
          value: this.formatSubscriptionDate(this.resolveSubscriptionDate([
            'subscriptionEndDate',
            'subscriptionEndAt',
            'subscriptionEnd',
            'endDate',
          ])),
        },
      ].map((detail) => {
        return {
          ...detail,
          value: detail.value || this.$t('menus.subscriptions.notAvailable'),
        };
      });
    },
  },
  methods: {
    resolveSubscriptionDate: function(keys) {
      const sources = [
        this.subscription,
        this.subscription?.subscription,
        this.subscription?.details,
        this.subscription?.period,
      ];

      for (const source of sources) {
        if (!source) {
          continue;
        }

        for (const key of keys) {
          if (source[key] !== undefined && source[key] !== null) {
            return source[key];
          }
        }
      }

      return null;
    },
    formatSubscriptionDate: function(value) {
      const date = this.toDate(value);

      if (!date) {
        return '';
      }

      return format(date, DATE_FORMAT);
    },
    toDate: function(value) {
      if (!value) {
        return null;
      }

      if (value instanceof Date && !Number.isNaN(value.valueOf())) {
        return value;
      }

      if (typeof value === 'number') {
        return this.timestampToDate(value);
      }

      if (typeof value === 'string') {
        const numericValue = Number(value);

        if (!Number.isNaN(numericValue) && value.trim() !== '') {
          return this.timestampToDate(numericValue);
        }

        const parsedDate = new Date(value);

        if (!Number.isNaN(parsedDate.valueOf())) {
          return parsedDate;
        }

        return null;
      }

      if (typeof value === 'object') {
        if ('days' in value || 'minutes' in value || 'hours' in value) {
          return null;
        }

        if (typeof value.toDate === 'function') {
          const date = value.toDate();

          if (date instanceof Date && !Number.isNaN(date.valueOf())) {
            return date;
          }
        }

        if (typeof value.seconds === 'number') {
          return this.timestampToDate(value.seconds);
        }
      }

      return null;
    },
    timestampToDate: function(timestamp) {
      const normalizedTimestamp = Number(timestamp);

      if (Number.isNaN(normalizedTimestamp)) {
        return null;
      }

      const isMilliseconds = normalizedTimestamp.toString().length >= 13;
      return new Date(isMilliseconds ? normalizedTimestamp : normalizedTimestamp * 1000);
    },
  },
}
</script>

<style scoped>
</style>
